import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Utensils, 
  Plus, 
  X, 
  ChevronLeft,
  ChevronRight,
  Coffee,
  Sun,
  Moon,
  Sparkles,
  Loader2,
  Trash2,
  ShoppingCart,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import VoiceInput from './VoiceInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Local storage key
const MEAL_CALENDAR_KEY = 'calm_keeper_meal_calendar';

const getMealCalendar = () => {
  const stored = localStorage.getItem(MEAL_CALENDAR_KEY);
  return stored ? JSON.parse(stored) : {};
};

const saveMealCalendar = (data) => {
  localStorage.setItem(MEAL_CALENDAR_KEY, JSON.stringify(data));
};

const mealTypes = [
  { id: 'breakfast', label: 'Breakfast', icon: Coffee, color: 'bg-normal-bg text-normal-text border-normal-border', placeholder: 'Eggs, toast, fruit...' },
  { id: 'lunch', label: 'Lunch', icon: Sun, color: 'bg-sage/20 text-sage-dark border-sage/30', placeholder: 'Sandwich, soup, salad...' },
  { id: 'dinner', label: 'Dinner', icon: Moon, color: 'bg-brown/20 text-brown border-brown/30', placeholder: 'Pasta, chicken, veggies...' },
];

const MealCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [mealData, setMealData] = useState(getMealCalendar());
  const [generating, setGenerating] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const navigate = useNavigate();
  
  const dateKey = currentDate.toISOString().split('T')[0];
  const dayMeals = mealData[dateKey] || {};
  const isToday = new Date().toDateString() === currentDate.toDateString();
  
  const goToPrevDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };
  
  const goToNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };
  
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  
  const handleMealChange = (mealType, value) => {
    const newData = { ...mealData };
    if (!newData[dateKey]) {
      newData[dateKey] = {};
    }
    if (value.trim() === '') {
      delete newData[dateKey][mealType];
    } else {
      newData[dateKey][mealType] = value;
    }
    setMealData(newData);
    setHasUnsavedChanges(true);
  };
  
  const handleSave = () => {
    saveMealCalendar(mealData);
    setHasUnsavedChanges(false);
    toast.success('Meals saved!');
  };
  
  const handleGenerateIdea = async (mealType) => {
    setGenerating(mealType);
    try {
      const response = await axios.post(`${API}/resources/meal-ideas`, {
        ingredients: ['pantry staples'],
        meal_type: mealType,
        dietary_restrictions: [],
      });
      
      if (response.data.ideas && response.data.ideas.length > 0) {
        const idea = response.data.ideas[0];
        handleMealChange(mealType, idea.name);
        toast.success('AI suggestion added!');
      }
    } catch (err) {
      toast.error('Could not generate idea');
    } finally {
      setGenerating(null);
    }
  };
  
  const handleClearMeal = (mealType) => {
    handleMealChange(mealType, '');
  };
  
  // Get week overview
  const getWeekOverview = () => {
    const overview = [];
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      const key = date.toISOString().split('T')[0];
      const meals = mealData[key] || {};
      const mealCount = Object.keys(meals).length;
      overview.push({
        date,
        key,
        mealCount,
        isSelected: key === dateKey,
        isToday: new Date().toDateString() === date.toDateString(),
      });
    }
    return overview;
  };
  
  const weekOverview = getWeekOverview();
  
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  return (
    <div className="space-y-6" data-testid="meal-calendar-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-serif text-2xl md:text-3xl text-ink-900 mb-2">
            Meal Planner
          </h2>
          <p className="text-ink-500 font-accent italic">
            Plan meals for your family 🍳
          </p>
        </div>
        <div className="w-12 h-12 rounded-full bg-brown/10 flex items-center justify-center border border-brown/20">
          <Utensils className="w-6 h-6 text-brown" />
        </div>
      </div>
      
      {/* Week Mini-Overview */}
      <Card className="p-4 bg-paper-light shadow-lifted border border-stone-200">
        <div className="flex justify-between items-center mb-3">
          <p className="text-xs text-ink-500 font-accent">This Week</p>
          <Button
            onClick={goToToday}
            variant="ghost"
            size="sm"
            className="text-xs text-sage hover:text-sage-dark h-6"
          >
            Today
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {weekOverview.map((day) => (
            <button
              key={day.key}
              onClick={() => setCurrentDate(day.date)}
              className={`
                p-2 rounded-lg text-center transition-all
                ${day.isSelected 
                  ? 'bg-brown text-white' 
                  : day.isToday 
                    ? 'bg-brown/10 text-brown' 
                    : 'hover:bg-stone-100'
                }
              `}
            >
              <p className="text-xs font-accent">
                {day.date.toLocaleDateString('en-US', { weekday: 'short' }).charAt(0)}
              </p>
              <p className={`text-sm font-medium ${day.isSelected ? 'text-white' : ''}`}>
                {day.date.getDate()}
              </p>
              <div className="flex justify-center gap-0.5 mt-1">
                {[0, 1, 2].map((i) => (
                  <div 
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full ${
                      i < day.mealCount 
                        ? day.isSelected ? 'bg-white' : 'bg-sage' 
                        : day.isSelected ? 'bg-white/30' : 'bg-stone-200'
                    }`}
                  />
                ))}
              </div>
            </button>
          ))}
        </div>
      </Card>
      
      {/* Day Navigation */}
      <div className="flex items-center justify-between">
        <Button
          onClick={goToPrevDay}
          variant="ghost"
          size="icon"
          className="text-ink-500 hover:text-brown"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        
        <div className="text-center">
          <h3 className={`font-serif text-xl ${isToday ? 'text-brown' : 'text-ink-900'}`}>
            {formatDate(currentDate)}
          </h3>
          {isToday && (
            <span className="text-xs text-brown font-accent">Today</span>
          )}
        </div>
        
        <Button
          onClick={goToNextDay}
          variant="ghost"
          size="icon"
          className="text-ink-500 hover:text-brown"
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>
      
      {/* Meal Cards - Day View */}
      <div className="space-y-4">
        {mealTypes.map(({ id, label, icon: Icon, color, placeholder }) => (
          <Card 
            key={id} 
            className={`p-4 shadow-lifted border ${color}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5" />
                <span className="font-serif text-lg">{label}</span>
              </div>
              <div className="flex gap-1">
                <Button
                  onClick={() => handleGenerateIdea(id)}
                  variant="ghost"
                  size="sm"
                  disabled={generating === id}
                  className="h-8 text-xs"
                >
                  {generating === id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-1" />
                      AI Idea
                    </>
                  )}
                </Button>
                {dayMeals[id] && (
                  <Button
                    onClick={() => handleClearMeal(id)}
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-ink-500 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            
            <div className="flex items-center justify-end mb-1">
              <VoiceInput onTranscription={(text) => handleMealChange(id, dayMeals[id] ? dayMeals[id] + ' ' + text : text)} />
            </div>
            <Textarea
              value={dayMeals[id] || ''}
              onChange={(e) => handleMealChange(id, e.target.value)}
              placeholder={placeholder}
              className="min-h-[80px] bg-white/50 border-stone-300 font-handwritten text-lg resize-none"
            />
          </Card>
        ))}
      </div>
      
      {/* Save Button */}
      {hasUnsavedChanges && (
        <Button
          onClick={handleSave}
          className="w-full vintage-btn btn-brown rounded-lg animate-fade-in"
        >
          <Save className="w-4 h-4 mr-2" />
          Save Changes
        </Button>
      )}
      
      {/* Shopping List Link */}
      <Card 
        className="p-4 bg-sage/10 border border-sage/20 cursor-pointer hover:bg-sage/20 transition-colors"
        onClick={() => navigate('/shopping')}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
            <ShoppingCart className="w-5 h-5 text-sage" />
          </div>
          <div className="flex-1">
            <p className="font-serif text-ink-900">Shopping List</p>
            <p className="text-xs text-ink-500 font-accent italic">
              Auto-generate shopping lists from your meal plan!
            </p>
          </div>
        </div>
      </Card>
      
      {/* Tips */}
      <Card className="p-4 bg-warmth-sand border border-stone-300">
        <p className="text-sm text-ink-700 font-handwritten text-lg text-center">
          🌿 Use the week overview to quickly jump between days!
        </p>
      </Card>
    </div>
  );
};

export default MealCalendar;
